"use client";
import {
  AreaChartIcon,
  ChevronDownIcon,
  HomeIcon,
  ListIcon,
  PlusIcon,
  SettingsIcon,
  ShieldIcon,
} from "lucide-react";
import Link from "next/link";
import { useContext, useEffect, useRef, useState } from "react";

import UserContext from "./UserProvider";
import JWTContext from "./JWTProvider";
import { FileMaker } from "./FileMaker";
const Navbar = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAllowedUser, setIsAllowedUser] = useState(false);
  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };
  const menuRef = useRef<HTMLDivElement>(null);

  const { user, setUser }: any = useContext(UserContext);
  const { JWT } = useContext(JWTContext);
  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setIsVisible(false);
    }
  };
  const useAutoLogout = (logoutFn: () => void, timeoutMinutes = 15) => {
        const timer = useRef<NodeJS.Timeout | null>(null);

        const resetTimer = () => {
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => {
            logoutFn();
          }, timeoutMinutes * 60 * 1000); // 15 minutes
        };

        const activityEvents = [
          "mousemove",
          "keydown",
          "mousedown",
          "touchstart",
          "scroll",
          "visibilitychange"
        ];

        useEffect(() => {
          const handleActivity = () => {
            if (document.visibilityState === "visible") {
              resetTimer();
            }
          };

          activityEvents.forEach((event) =>
            document.addEventListener(event, handleActivity)
          );

          resetTimer();

          return () => {
            if (timer.current) clearTimeout(timer.current);
            activityEvents.forEach((event) =>
              document.removeEventListener(event, handleActivity)
            );
          };
        }, [logoutFn, timeoutMinutes]);
      };

  const signOut = () => {
    setUser(null);
    sessionStorage.removeItem("userData");
    sessionStorage.removeItem("policyBoardMtgId");
    sessionStorage.removeItem("isItBoard");
    window.location.href="";
  };
  useAutoLogout(signOut, 15);
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

useEffect(() => {
  const userStr = sessionStorage.getItem("userData");

  if (userStr) {
    const parsedUser = JSON.parse(userStr);
    setUser(parsedUser);
    
    // Verify user role via API
    if (JWT) {
      verifyUserRoleViaAPI(parsedUser.cms_id);
    }
  }
}, [JWT, setUser]);

const verifyUserRoleViaAPI = async (cmsId: string) => {
  try {
    if (!JWT) return;

    const fileMaker = new FileMaker(JWT);
    const userData = await fileMaker.findUserByCmsId(cmsId);

    const allowedRoles = ["Member", "Board Rep", "Editor", "CMS Staff"];

    if (!userData || !userData.usertype || !allowedRoles.includes(userData.usertype)) {
      window.location.href = "https://www.cms.org/central-line/not-allowed";
      return;
    }

    setIsAllowedUser(true);

    // Check if user is staff (Editor or CMS Staff)
    if (["Editor", "CMS Staff"].includes(userData.usertype)) {
      sessionStorage.setItem("isStaff", "true");
    } else {
      sessionStorage.removeItem("isStaff");
    }
  } catch (err) {
    console.error("Error verifying user role:", err);
    window.location.href = "https://www.cms.org/central-line/not-allowed";
  }
};

  return (
    <>
      <nav className="bg-gray-100 border-b border-gray-300 p-4 text-gray-500 h-14 text-sm">
        <ul className="flex space-x-4">
          <li>
            <Link
              href="/"
              className="flex flex-row gap-1 items-center justify-center hover:text-gray-800"
            >
              <HomeIcon size={14} />
              Home
            </Link>
          </li>
          
     {user?(<>
          <li>
            <Link
              href="https://www.cms.org/central-line/submit"
              className="flex flex-row gap-1 items-center justify-center hover:text-gray-800"
            >
              <PlusIcon size={14} />
              Submit Proposal
            </Link>
          </li>
          <li>
            <Link
              href="/current-proposals"
              className="flex flex-row gap-1 items-center justify-center hover:text-gray-800"
            >
              <ListIcon size={14} />
              Current Proposals
            </Link>
          </li>
          <li>
            <Link
              href="/board-summaries"
              className="flex flex-row gap-1 items-center justify-center hover:text-gray-800"
            >
              <AreaChartIcon size={14} />
              Board Summaries
            </Link>
          </li>
          {sessionStorage.getItem("isStaff") === "true" && isAllowedUser && (
            <li>
              <Link
                href="/staff"
                className="flex flex-row gap-1 items-center justify-center hover:text-gray-800"
              >
                <ShieldIcon size={14} />
                Staff
              </Link>
            </li>
          )}
          <li>
            <Link
              href="/my-profile"
              className="flex flex-row gap-1 items-center justify-center hover:text-gray-800"
            >
              <SettingsIcon size={14} />
              My Profile
            </Link>
          </li>
          <li>
            <Link
              href="#"
              onClick={toggleVisibility}
              className="flex flex-row gap-1 items-center justify-center hover:text-gray-800"
            >
              Resources
              <ChevronDownIcon size={14} />
            </Link>
            {isVisible && (
              <div
                ref={menuRef}
                className="absolute top-14 bg-white p-3 border shadow-lg"
              >
                <ul className="flex flex-col gap-3">
                  <li>
                    <Link
                      href="/why"
                      className="flex flex-row gap-1 items-start justify-start hover:text-gray-800"
                    >
                      About Central Line
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/guidance"
                      className="flex flex-row gap-1 items-start justify-start hover:text-gray-800"
                    >
                      Process & Guidance
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="https://cms.org/about/policies"
                      className="flex flex-row gap-1 items-start justify-start hover:text-gray-800"
                      target="_blank"
                    >
                      Current Policies
                    </Link>
                  </li>
                </ul>
              </div>
            )}
          </li>
        </>
        ):''} 
          {user === null ? (
            <li className="flex flex-1 justify-end hover:text-gray-800">
              <Link href="/sign-in">Sign In</Link>
            </li>
          ) : (
            <li
              className="flex flex-1 justify-end hover:text-gray-800 hover:cursor-pointer overflow-x-hidden"
              onClick={signOut}
            >
              Sign Out ({user!.display_name})
            </li>
          )}
        </ul>
      </nav>
    </>
  );
};

export default Navbar;
